/**
 * EFFORT bounded context — what a person actually did.
 *
 * PLANNING owns what was intended (the authoring tree, the flattened
 * `SessionPlan`, the activity catalog, and the manual dosage entry rules); this
 * file owns what was performed, and it is the only place allowed to mint a
 * history record. Two invariants run through everything below and explain most
 * of the shapes:
 *
 *  1. Nothing is inferred. Perceived effort, pause time, rounds, and outcome are
 *     stored facts or they are absent. The Swift app has no calorie model, no
 *     intensity model, and no streak; deriving one here would be a parity break,
 *     not a feature.
 *  2. Ending is fail-closed. A finished effort is either archived or explicitly
 *     discarded by the user, and the archive path is idempotent so a retry after
 *     a storage failure cannot mint a second record or move the recorded end
 *     time to the retry instant.
 */

import type { Milliseconds } from "./time";
import { SECOND } from "./time";
import type {
  ActivitySnapshot,
  ManualEffortDosage,
  SegmentKind,
  SessionPlan,
  SessionSegment,
  SessionSegmentId,
  WorkoutMode,
  WorkoutPlanId,
} from "./planning";
import { isBoxingRoundSegment, manualEffortWallDuration } from "./planning";
// The full authored call, retained on a live checkpoint so a restored session
// resumes the same combination instead of generating a different one.
import type { ComboScore } from "./combat";

/* ------------------------------------------------------------------------- *
 * Instants and branded identity
 * ------------------------------------------------------------------------- */

declare const effortInstantBrand: unique symbol;

/**
 * Absolute time as epoch **microseconds**, branded so it cannot be mistaken for a
 * `Milliseconds` duration or for a legacy epoch-millisecond value.
 *
 * The canonical effort boundary promises to retain device timestamp provenance
 * below a millisecond, so its serializer writes a single integer of epoch
 * microseconds — the same encoding as `canonicalizeJournalInstant`. Content
 * hashes and verified read-back are built on those bytes, which is why the unit
 * belongs to the type rather than to a convention a serializer has to guess.
 *
 * Durations stay in `Milliseconds`, so every crossing is written out:
 * `endedAt = startedAt + wall * EFFORT_MICROSECONDS_PER_MILLISECOND`, spelled
 * once in {@link effortInstantAdvancedBy}. The arithmetic still happens on the
 * absolute timeline, never on wall-clock components, which is what keeps an
 * entry spanning a DST transition ending at the right real-world instant.
 */
export type EffortInstant = number & { readonly [effortInstantBrand]: true };

export const EFFORT_MICROSECONDS_PER_MILLISECOND = 1_000;

/**
 * The canonical encoding writes one integer, so the representable range is the
 * range in which a JavaScript number is still an exact integer. Anything wider
 * would encode and decode to different bytes and fail write verification.
 */
export const EFFORT_INSTANT_MIN = -Number.MAX_SAFE_INTEGER as EffortInstant;
export const EFFORT_INSTANT_MAX = Number.MAX_SAFE_INTEGER as EffortInstant;

export const EFFORT_CANONICAL_DATE_RANGE_ERROR =
  "Date is outside canonical epoch-microsecond range";

export function isEffortInstant(value: number): value is EffortInstant {
  return (
    Number.isInteger(value) && value >= EFFORT_INSTANT_MIN && value <= EFFORT_INSTANT_MAX
  );
}

/**
 * Rounds half away from zero, matching the Swift encoder, so an in-memory value
 * compared against its own re-decoded copy cannot differ by a sub-microsecond
 * fraction that never survived encoding.
 */
export function canonicalizeEffortInstant(epochMicroseconds: number): EffortInstant {
  const rounded =
    epochMicroseconds < 0 ? -Math.round(-epochMicroseconds) : Math.round(epochMicroseconds);
  if (!isEffortInstant(rounded)) {
    throw new RangeError(EFFORT_CANONICAL_DATE_RANGE_ERROR);
  }
  return rounded as EffortInstant;
}

export function effortInstantFromMilliseconds(epochMilliseconds: number): EffortInstant {
  return canonicalizeEffortInstant(epochMilliseconds * EFFORT_MICROSECONDS_PER_MILLISECOND);
}

export function effortInstantToMilliseconds(instant: EffortInstant): number {
  return instant / EFFORT_MICROSECONDS_PER_MILLISECOND;
}

/** The only place a duration is allowed to move an instant, so the millisecond →
 *  microsecond conversion exists exactly once. */
export function effortInstantAdvancedBy(
  instant: EffortInstant,
  duration: Milliseconds,
): EffortInstant {
  return canonicalizeEffortInstant(instant + duration * EFFORT_MICROSECONDS_PER_MILLISECOND);
}

/** `Math.max` loses the brand; this keeps it without an assertion at a call site. */
export function laterEffortInstant(left: EffortInstant, right: EffortInstant): EffortInstant {
  return left >= right ? left : right;
}

declare const effortSessionIdBrand: unique symbol;
declare const executionEntryIdBrand: unique symbol;
declare const effortCheckpointIdBrand: unique symbol;
declare const legacyWorkoutRecordIdBrand: unique symbol;

/** Canonical effort record identity. Stable across manual edits and archive retries. */
export type EffortSessionId = string & { readonly [effortSessionIdBrand]: true };

/** Derived, never allocated — see {@link deriveExecutionEntryId}. */
export type ExecutionEntryId = string & { readonly [executionEntryIdBrand]: true };

/** Live/checkpoint identity, deliberately distinct from the record it may become. */
export type EffortCheckpointId = string & { readonly [effortCheckpointIdBrand]: true };

/** Read-only legacy `WorkoutRecord` identity; a migration input, never written. */
export type LegacyWorkoutRecordId = string & { readonly [legacyWorkoutRecordIdBrand]: true };

export function effortSessionId(raw: string): EffortSessionId {
  return raw as EffortSessionId;
}

export function effortCheckpointId(raw: string): EffortCheckpointId {
  return raw as EffortCheckpointId;
}

export function legacyWorkoutRecordId(raw: string): LegacyWorkoutRecordId {
  return raw as LegacyWorkoutRecordId;
}

/* ------------------------------------------------------------------------- *
 * Schema versions and limits
 * ------------------------------------------------------------------------- */

export const EFFORT_SESSION_SCHEMA_VERSION = 2;
export const SESSION_REFLECTION_SCHEMA_VERSION = 2;
export const EFFORT_CHECKPOINT_SCHEMA_VERSION = 2;
export const LEGACY_WORKOUT_RECORD_SCHEMA_VERSION = 2;

/** IANA offsets never exceed ±18 h; anything wider is corrupt timing evidence. */
export const MAXIMUM_GMT_OFFSET_SECONDS = 18 * 60 * 60;

export const CORNER_NOTE_CHARACTER_LIMIT = 1_200;

/** The weekly rhythm chart saturates rather than rescaling, so one long session
 *  cannot flatten the rest of the week into invisibility. */
export const WEEKLY_RHYTHM_MINUTE_CEILING = 60;

/** Halfway is only announced on a segment long enough for a midpoint to mean
 *  something; on a 15 s burst the cue would land on top of the transition. */
export const HALFWAY_MINIMUM_SEGMENT_DURATION: Milliseconds = 20 * SECOND;

/* ------------------------------------------------------------------------- *
 * Source, outcome, provenance
 * ------------------------------------------------------------------------- */

export type EffortSource = "inAppPlan" | "inAppDuration" | "manual" | "healthKit";

export type EffortOutcome = "completed" | "partial" | "recorded";

export type DeviceAuthority = "phone" | "mac" | "watch" | "external" | "unknown";

/**
 * Where the bytes came from. External fields are optional rather than empty
 * strings, because "imported from something unnamed" and "not imported" are
 * different facts and only one of them should render a source line.
 */
export interface EffortProvenance {
  readonly deviceAuthority: DeviceAuthority;
  readonly externalSourceName?: string;
  readonly externalIdentifier?: string;
}

/** Enough to re-open or repeat the plan without pinning the record to a mutable
 *  authoring tree that may since have been edited or deleted. */
export interface PlanReference {
  readonly id: WorkoutPlanId;
  readonly revision?: number;
  readonly name: string;
}

/* ------------------------------------------------------------------------- *
 * Timing evidence
 * ------------------------------------------------------------------------- */

export type ActiveDurationBasis =
  | "measuredMonotonic"
  | "userEntered"
  | "externalReported"
  | "legacyEstimated";

export type WallDurationBasis =
  | "measuredMonotonic"
  | "assumedEqualToActive"
  | "activePlusEnteredPause"
  | "derivedFromTimestamps"
  | "legacyEstimated";

/**
 * The distinction this union exists for: `notProvided` means nobody ever told us
 * about pauses, while `userEntered` with 0 ms is an asserted zero. The first must
 * never render "0:00 paused"; the second may.
 */
export type PauseAccounting =
  | { readonly kind: "notProvided" }
  | { readonly kind: "userEntered"; readonly milliseconds: Milliseconds };

export const PAUSE_NOT_PROVIDED: PauseAccounting = Object.freeze({ kind: "notProvided" } as const);

export function userEnteredPause(milliseconds: Milliseconds): PauseAccounting {
  return { kind: "userEntered", milliseconds: Math.max(0, Math.trunc(milliseconds)) };
}

/**
 * Required for manual records, absent for engine-written and legacy ones. It is
 * what lets History and Summary decide whether Elapsed and Paused are provable at
 * all, rather than guessing from `wall - active`.
 */
export interface EffortTimingEvidence {
  readonly activeBasis: ActiveDurationBasis;
  readonly wallBasis: WallDurationBasis;
  readonly pauseAccounting: PauseAccounting;
  readonly durationInputResolutionMilliseconds: Milliseconds;
  /** The zone the user selected while entering, frozen into the record. */
  readonly entryTimeZoneIdentifier?: string;
  /** The offset that zone had *at this session's start*, not today's offset — a
   *  tz-database update must not retroactively move an old record. */
  readonly entryGMTOffsetSecondsAtStart?: number;
}

export function hasUserEnteredPause(evidence: EffortTimingEvidence | undefined): boolean {
  return evidence?.pauseAccounting.kind === "userEntered";
}

export function enteredPauseDuration(
  evidence: EffortTimingEvidence | undefined,
): Milliseconds | undefined {
  const accounting = evidence?.pauseAccounting;
  return accounting?.kind === "userEntered" ? accounting.milliseconds : undefined;
}

/* ------------------------------------------------------------------------- *
 * Execution entries
 * ------------------------------------------------------------------------- */

export type ExecutionDisposition =
  | "completedNaturally"
  | "skipped"
  | "endedEarly"
  | "manualCompletion"
  | "legacyUnknown";

/** `legacyEstimated` entries are readable but never archivable — they exist so a
 *  migrated record can be displayed without pretending its numbers were measured. */
export type ExecutionFidelity = "exact" | "legacyEstimated";

/**
 * One occupancy of a planned step. Offsets are measured into the session's
 * *active* duration, not wall time, because a paused clock would otherwise open a
 * hole that later visits appear to overlap.
 */
export interface ExecutionVisit {
  readonly offsetMilliseconds: Milliseconds;
  readonly durationMilliseconds: Milliseconds;
}

/** What the engine was calling while the step was performed. */
export type ExecutionGuidance =
  | { readonly kind: "combo"; readonly comboId: ComboScore["id"]; readonly spokenPrompt: string }
  | { readonly kind: "drill"; readonly drillName: string };

export interface ExecutionEntry {
  readonly id: ExecutionEntryId;
  /** Index into `SessionPlan.segments`; part of the id derivation, so it also
   *  disambiguates two occurrences of one repeated authored interval. */
  readonly planIndex: number;
  readonly segmentId: SessionSegmentId;
  /** This step's own monotonic ledger — revisiting a step adds, never resets. */
  readonly performedMilliseconds: Milliseconds;
  readonly disposition: ExecutionDisposition;
  readonly visits: readonly ExecutionVisit[];
  readonly guidance?: ExecutionGuidance;
  readonly fidelity: ExecutionFidelity;
}

/* ------------------------------------------------------------------------- *
 * Deterministic execution-entry identity
 * ------------------------------------------------------------------------- */

const FNV_1A_128_OFFSET_BASIS = 0x6c62272e07bb014262b821756295c58dn;
const FNV_1A_128_PRIME = 0x0000000001000000000000000000013bn;
const MASK_128 = (1n << 128n) - 1n;

/**
 * UTF-8 encoded by hand rather than through `TextEncoder`: the domain layer must
 * not reach for platform globals, and the derivation has to produce identical
 * bytes in a worker, in Node, and in a test runner.
 */
function utf8Bytes(value: string): number[] {
  const bytes: number[] = [];
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint < 0x80) {
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      bytes.push(0xc0 | (codePoint >> 6), 0x80 | (codePoint & 0x3f));
    } else if (codePoint < 0x10000) {
      bytes.push(
        0xe0 | (codePoint >> 12),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    } else {
      bytes.push(
        0xf0 | (codePoint >> 18),
        0x80 | ((codePoint >> 12) & 0x3f),
        0x80 | ((codePoint >> 6) & 0x3f),
        0x80 | (codePoint & 0x3f),
      );
    }
  }
  return bytes;
}

function fnv1a128(value: string): bigint {
  let hash = FNV_1A_128_OFFSET_BASIS;
  for (const byte of utf8Bytes(value)) {
    hash = (hash ^ BigInt(byte)) & MASK_128;
    hash = (hash * FNV_1A_128_PRIME) & MASK_128;
  }
  return hash;
}

/**
 * The exact key the engine hashes. It is spelled out as its own function because
 * changing a single separator would silently re-identify every entry and turn an
 * idempotent archive retry into a duplicate write.
 */
export function executionEntryKey(
  sessionId: EffortSessionId,
  planIndex: number,
  segmentId: SessionSegmentId,
): string {
  return `${sessionId}|current-step|${planIndex}|${segmentId}`;
}

/**
 * Entry ids are derived, never random. A retry after a failed history write
 * recomputes the same ids from the same session, so the second attempt overwrites
 * the first attempt's rows instead of accumulating ghosts.
 *
 * The digest is FNV-1a/128 shaped into an RFC 4122 v5 layout: it is deliberately
 * not a real SHA-1 name-based UUID, and the version/variant nibbles exist only so
 * the value round-trips through UUID-typed storage unchanged.
 */
export function deriveExecutionEntryId(
  sessionId: EffortSessionId,
  planIndex: number,
  segmentId: SessionSegmentId,
): ExecutionEntryId {
  const digest = fnv1a128(executionEntryKey(sessionId, planIndex, segmentId));
  const bytes: number[] = [];
  for (let index = 0; index < 16; index += 1) {
    bytes[index] = Number((digest >> BigInt((15 - index) * 8)) & 0xffn);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x50;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = bytes.map((byte) => byte.toString(16).padStart(2, "0")).join("");
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join("-") as ExecutionEntryId;
}

/* ------------------------------------------------------------------------- *
 * Rounding reconciliation
 * ------------------------------------------------------------------------- */

/**
 * Redistributes per-entry durations so they sum to the session's active duration
 * exactly.
 *
 * The engine measures each step independently and the session total separately,
 * so the two disagree by a few milliseconds on almost every session. Persisting
 * that disagreement would make `Σ entries === activeDuration` unenforceable and
 * every downstream breakdown subtly wrong, so the entries are reconciled *to* the
 * session total, which is the measured authority.
 *
 * Largest-remainder apportionment keeps the result order-independent and stable:
 * identical inputs always produce identical output, which is what makes an archive
 * retry byte-identical to the attempt that failed. The arithmetic runs in BigInt
 * because `weight × total` for a multi-hour session exceeds the range a double can
 * represent exactly.
 */
export function reconcileExecutionDurations(
  rawDurations: readonly Milliseconds[],
  activeDuration: Milliseconds,
): Milliseconds[] {
  if (rawDurations.length === 0) return [];

  const target = Math.max(0, Math.trunc(activeDuration));
  const weights = rawDurations.map((value) => Math.max(0, Math.trunc(value)));
  const weightTotal = weights.reduce((sum, value) => sum + value, 0);
  if (weightTotal === target) return weights;

  // A zero-weight ledger against a non-zero total means the per-step measurements
  // were lost but the session total survived; spread it evenly rather than
  // attributing the whole session to step zero.
  const effectiveWeights = weightTotal === 0 ? weights.map(() => 1) : weights;
  const effectiveTotal = BigInt(weightTotal === 0 ? weights.length : weightTotal);
  const targetBig = BigInt(target);

  const floors: number[] = [];
  const remainders: bigint[] = [];
  for (const weight of effectiveWeights) {
    const numerator = BigInt(weight) * targetBig;
    floors.push(Number(numerator / effectiveTotal));
    remainders.push(numerator % effectiveTotal);
  }

  let shortfall = target - floors.reduce((sum, value) => sum + value, 0);
  const order = floors
    .map((_, index) => index)
    .sort((left, right) => {
      const leftRemainder = remainders[left] ?? 0n;
      const rightRemainder = remainders[right] ?? 0n;
      if (leftRemainder !== rightRemainder) return leftRemainder > rightRemainder ? -1 : 1;
      return left - right;
    });

  let cursor = 0;
  while (shortfall > 0 && order.length > 0) {
    const index = order[cursor % order.length] ?? 0;
    floors[index] = (floors[index] ?? 0) + 1;
    shortfall -= 1;
    cursor += 1;
  }

  return floors;
}

/**
 * Applies {@link reconcileExecutionDurations} to entries, preserving their order,
 * and carries the same correction down into each entry's visit ledger.
 *
 * Rewriting an entry's total without rewriting its visits would break the record's
 * own `Σ visits === entry duration` invariant precisely when reconciliation did
 * its job, so each entry absorbs its delta into its own visits. A visit whose
 * apportioned share rounds to zero occupied no active time and is dropped rather
 * than kept as a zero-width span — which also makes the offsets below strictly
 * increasing, and therefore the whole relayout stable under a second application.
 *
 * Offsets are then re-laid on the active timeline in the visits' original
 * chronological order, so visits stay monotonic inside an entry, never overlap
 * across entries, and never run past `activeDuration`.
 */
export function reconcileExecutionEntries(
  entries: readonly ExecutionEntry[],
  activeDuration: Milliseconds,
): ExecutionEntry[] {
  const reconciled = reconcileExecutionDurations(
    entries.map((entry) => entry.performedMilliseconds),
    activeDuration,
  );

  const rescaled = entries.map((entry, index) => {
    const performed = reconciled[index] ?? 0;
    if (entry.visits.length === 0) {
      return { ...entry, performedMilliseconds: performed };
    }
    const visitDurations = reconcileExecutionDurations(
      entry.visits.map((visit) => visit.durationMilliseconds),
      performed,
    );
    return {
      ...entry,
      performedMilliseconds: performed,
      visits: entry.visits
        .map((visit, visitIndex) => ({
          offsetMilliseconds: visit.offsetMilliseconds,
          durationMilliseconds: visitDurations[visitIndex] ?? 0,
        }))
        .filter((visit) => visit.durationMilliseconds > 0),
    };
  });

  const chronological = rescaled
    .flatMap((entry, entryIndex) =>
      entry.visits.map((visit, visitIndex) => ({
        entryIndex,
        visitIndex,
        offsetMilliseconds: visit.offsetMilliseconds,
      })),
    )
    .sort(
      (left, right) =>
        left.offsetMilliseconds - right.offsetMilliseconds ||
        left.entryIndex - right.entryIndex ||
        left.visitIndex - right.visitIndex,
    );

  const offsets = rescaled.map((entry) => entry.visits.map(() => 0));
  let cursor = 0;
  for (const position of chronological) {
    const row = offsets[position.entryIndex];
    const visit = rescaled[position.entryIndex]?.visits[position.visitIndex];
    if (row === undefined || visit === undefined) continue;
    row[position.visitIndex] = cursor;
    cursor += visit.durationMilliseconds;
  }

  return rescaled.map((entry, entryIndex) => ({
    ...entry,
    visits: entry.visits.map((visit, visitIndex) => ({
      offsetMilliseconds: offsets[entryIndex]?.[visitIndex] ?? visit.offsetMilliseconds,
      durationMilliseconds: visit.durationMilliseconds,
    })),
  }));
}

export function executionEntriesReconcile(
  entries: readonly ExecutionEntry[],
  activeDuration: Milliseconds,
): boolean {
  const total = entries.reduce((sum, entry) => sum + entry.performedMilliseconds, 0);
  return total === Math.max(0, Math.trunc(activeDuration));
}

/* ------------------------------------------------------------------------- *
 * Migration envelope and the execution ledger
 * ------------------------------------------------------------------------- */

/**
 * Present only on records adapted from the legacy `WorkoutRecord` store. Its
 * presence is a licence to skip execution validation: the legacy bytes were never
 * held to the per-entry contract, and rejecting them would delete history rather
 * than repair it.
 */
export interface EffortMigrationMetadata {
  readonly legacyRecordId: LegacyWorkoutRecordId;
  readonly adaptedAt: EffortInstant;
  /** Trusted over recomputation: the legacy record counted rounds in a way the
   *  current entry-based rule cannot reconstruct. */
  readonly legacyCompletedRounds?: number;
  readonly legacySkippedCombos?: number;
}

/**
 * The engine's running tally. Monotonic and cursor-independent — stepping back
 * never subtracts performed time or clears a completed id — so it may legitimately
 * exceed the plan's duration when a user revisits work.
 */
export interface WorkoutExecutionLedger {
  readonly performedDuration: Milliseconds;
  /** Zero and negative values are dropped rather than stored, so a key's presence
   *  is itself the claim that the segment was performed. */
  readonly performedBySegment: Readonly<Record<SessionSegmentId, Milliseconds>>;
  readonly completedSegmentIds: readonly SessionSegmentId[];
  readonly skippedSegmentIds: readonly SessionSegmentId[];
}

export const EMPTY_EXECUTION_LEDGER: WorkoutExecutionLedger = Object.freeze({
  performedDuration: 0,
  performedBySegment: Object.freeze({}),
  completedSegmentIds: Object.freeze([]),
  skippedSegmentIds: Object.freeze([]),
});

/* ------------------------------------------------------------------------- *
 * Segment predicates
 * ------------------------------------------------------------------------- */

/**
 * Preparation and recovery are excluded because skipping a rest or a countdown is
 * not a failure to train, and a zero-duration segment is excluded because there
 * was nothing to perform.
 */
export function isRequiredSegment(segment: SessionSegment): boolean {
  return segment.duration > 0 && segment.kind !== "preparation" && segment.kind !== "recovery";
}

export function segmentRepetitionTarget(segment: SessionSegment): number | undefined {
  return segment.target.mode === "repetitions" ? segment.target.repetitions : undefined;
}

/* ------------------------------------------------------------------------- *
 * Outcome normalization
 * ------------------------------------------------------------------------- */

export interface SegmentExecutionSummary {
  readonly segment: SessionSegment;
  readonly skipped: boolean;
  readonly completedNaturallyMilliseconds: Milliseconds;
  readonly hasManualCompletion: boolean;
  readonly performedMilliseconds: Milliseconds;
}

export function summarizeSegmentExecution(
  segments: readonly SessionSegment[],
  entries: readonly ExecutionEntry[],
): SegmentExecutionSummary[] {
  return segments.map((segment) => {
    const segmentEntries = entries.filter((entry) => entry.segmentId === segment.id);
    return {
      segment,
      skipped: segmentEntries.some((entry) => entry.disposition === "skipped"),
      completedNaturallyMilliseconds: segmentEntries
        .filter((entry) => entry.disposition === "completedNaturally")
        .reduce((sum, entry) => sum + entry.performedMilliseconds, 0),
      hasManualCompletion: segmentEntries.some((entry) => entry.disposition === "manualCompletion"),
      performedMilliseconds: segmentEntries.reduce(
        (sum, entry) => sum + entry.performedMilliseconds,
        0,
      ),
    };
  });
}

export interface OutcomeNormalizationInput {
  readonly requested: EffortOutcome;
  readonly source: EffortSource;
  readonly hasExecutedPlan: boolean;
  readonly hasMigration: boolean;
  readonly segments: readonly SegmentExecutionSummary[];
  /** The engine's own record of what was earned. Completion is read from here, not
   *  re-derived from durations — see {@link normalizeEffortOutcome}. */
  readonly ledger: WorkoutExecutionLedger;
}

/**
 * "Completed" is a claim about a plan, so it has to be earned against one.
 *
 * A manual or imported record has no plan to complete — it can only assert that
 * effort happened — which is why `recorded` exists as a third outcome instead of
 * forcing those records to choose between completed and partial. A migrated
 * record keeps whatever the legacy store recorded, because recomputing a verdict
 * from entries the legacy store never validated would fabricate one.
 *
 * Completion is a **set-membership test against the engine's ledger**, never a
 * duration comparison. Entry durations are reconciled to the session total before
 * a record is sealed, so a session whose per-step measurements summed a few
 * milliseconds above the session clock has every entry scaled *down* — and a
 * duration threshold would then archive a fully completed workout as `partial`,
 * permanently.
 */
export function normalizeEffortOutcome(input: OutcomeNormalizationInput): EffortOutcome {
  if (
    input.requested === "completed" &&
    !input.hasExecutedPlan &&
    (input.source === "manual" || input.source === "healthKit")
  ) {
    return "recorded";
  }

  if (input.requested !== "completed" || !input.hasExecutedPlan || input.hasMigration) {
    return input.requested;
  }

  const required = input.segments.filter((summary) => isRequiredSegment(summary.segment));
  const everyRequiredSegmentEarned = required.every((summary) => {
    const id = summary.segment.id;
    if (input.ledger.skippedSegmentIds.includes(id) || summary.skipped) return false;
    if (input.ledger.completedSegmentIds.includes(id)) return true;
    // A rep target has no honest time threshold — the user, not the clock, decides
    // when the work is done — so an explicit "reps done" tap completes it.
    return segmentRepetitionTarget(summary.segment) !== undefined && summary.hasManualCompletion;
  });

  return everyRequiredSegmentEarned ? "completed" : "partial";
}

/* ------------------------------------------------------------------------- *
 * The canonical record, and the seal that guards it
 * ------------------------------------------------------------------------- */

export interface EffortSessionFields {
  readonly id: EffortSessionId;
  readonly schemaVersion: number;
  /** 1 for engine-written records; incremented once per confirmed manual edit. */
  readonly revision: number;
  readonly activity: ActivitySnapshot;
  readonly source: EffortSource;
  readonly provenance: EffortProvenance;
  readonly startedAt: EffortInstant;
  readonly endedAt: EffortInstant;
  readonly wallDurationMilliseconds: Milliseconds;
  readonly activeDurationMilliseconds: Milliseconds;
  readonly outcome: EffortOutcome;
  readonly originPlan?: PlanReference;
  /** The immutable snapshot the engine actually ran, so later edits to the saved
   *  plan cannot retroactively change what this record says was executed. */
  readonly executedPlan?: SessionPlan;
  readonly executionEntries: readonly ExecutionEntry[];
  readonly migration?: EffortMigrationMetadata;
  readonly timingEvidence?: EffortTimingEvidence;
  readonly createdAt: EffortInstant;
  readonly updatedAt: EffortInstant;
}

declare const effortArchiveSeal: unique symbol;

/**
 * The seal is a phantom: declared, never assigned, absent at runtime and from
 * serialized bytes. Its only job is to make `EffortSession` unconstructible from
 * an object literal, so the only ways to obtain one are {@link sealEffortSession}
 * — which accepts an {@link ArchivedTermination} and nothing else — and
 * {@link rehydrateEffortSession}, which re-reads rows that are already history.
 *
 * That is what makes the discard path provable rather than merely documented: a
 * {@link DiscardedEffort} carries no outcome and no durations, no function maps
 * one to an `ArchivedTermination`, and therefore no discarded effort has any path
 * into History.
 */
export type EffortSession = EffortSessionFields & {
  readonly [effortArchiveSeal]: "archived";
};

/* ------------------------------------------------------------------------- *
 * Termination: archive or discard
 * ------------------------------------------------------------------------- */

export interface ArchivedTermination {
  readonly kind: "archived";
  readonly sessionId: EffortSessionId;
  /**
   * Frozen at the instant the effort actually ended. A retry after a failed
   * history write re-uses this value rather than "now", so a session archived on
   * the third attempt does not claim the user trained until then.
   */
  readonly endedAt: EffortInstant;
}

export interface DiscardedTermination {
  readonly kind: "discarded";
  readonly sessionId: EffortSessionId;
  readonly discardedAt: EffortInstant;
}

export type EffortTermination = ArchivedTermination | DiscardedTermination;

/**
 * A discarded effort is fully representable — it has to be, because the checkpoint
 * must remember that this session was deliberately thrown away so a resume cannot
 * revive it — but it deliberately carries no outcome, no durations, and no
 * entries. There is nothing here for a history record to be built from.
 */
export interface DiscardedEffort {
  readonly sessionId: EffortSessionId;
  readonly activity: ActivitySnapshot;
  readonly startedAt: EffortInstant;
  readonly discardedAt: EffortInstant;
}

export function isArchivable(termination: EffortTermination): termination is ArchivedTermination {
  return termination.kind === "archived";
}

export function discardEffort(input: {
  readonly sessionId: EffortSessionId;
  readonly activity: ActivitySnapshot;
  readonly startedAt: EffortInstant;
  readonly discardedAt: EffortInstant;
}): DiscardedEffort {
  return input;
}

export interface EffortSessionDraft {
  readonly id: EffortSessionId;
  readonly revision: number;
  readonly activity: ActivitySnapshot;
  readonly source: EffortSource;
  readonly provenance: EffortProvenance;
  readonly startedAt: EffortInstant;
  readonly wallDurationMilliseconds: Milliseconds;
  readonly activeDurationMilliseconds: Milliseconds;
  readonly requestedOutcome: EffortOutcome;
  readonly originPlan?: PlanReference;
  readonly executedPlan?: SessionPlan;
  readonly executionEntries: readonly ExecutionEntry[];
  /** The engine's completed/skipped ids, carried into the seal because the outcome
   *  is read from them. A planless draft supplies {@link EMPTY_EXECUTION_LEDGER}. */
  readonly ledger: WorkoutExecutionLedger;
  readonly migration?: EffortMigrationMetadata;
  readonly timingEvidence?: EffortTimingEvidence;
  readonly createdAt: EffortInstant;
  readonly updatedAt: EffortInstant;
}

/**
 * The single door into History for live effort.
 *
 * Everything it does is a clamp or a derivation the record's own invariants
 * already require, so calling it twice on the same draft and termination yields
 * byte-identical output — which is exactly what makes the archive transaction safe
 * to retry after a storage failure.
 */
export function sealEffortSession(
  termination: ArchivedTermination,
  draft: EffortSessionDraft,
): EffortSession {
  const startedAt = canonicalizeEffortInstant(draft.startedAt);
  const endedAt = laterEffortInstant(startedAt, canonicalizeEffortInstant(termination.endedAt));
  const wall = Math.max(0, Math.trunc(draft.wallDurationMilliseconds));
  const active = Math.min(wall, Math.max(0, Math.trunc(draft.activeDurationMilliseconds)));

  // Migrated entries are left exactly as the legacy store wrote them; reconciling
  // them would invent per-step precision that record never had.
  const entries = draft.migration
    ? draft.executionEntries
    : reconcileExecutionEntries(draft.executionEntries, active);

  const outcome = normalizeEffortOutcome({
    requested: draft.requestedOutcome,
    source: draft.source,
    hasExecutedPlan: draft.executedPlan !== undefined,
    hasMigration: draft.migration !== undefined,
    segments: summarizeSegmentExecution(draft.executedPlan?.segments ?? [], entries),
    ledger: draft.ledger,
  });

  const createdAt = canonicalizeEffortInstant(draft.createdAt);
  const fields: EffortSessionFields = {
    id: termination.sessionId,
    schemaVersion: EFFORT_SESSION_SCHEMA_VERSION,
    revision: Math.max(1, Math.trunc(draft.revision)),
    activity: draft.activity,
    source: draft.source,
    provenance: draft.provenance,
    startedAt,
    endedAt,
    wallDurationMilliseconds: wall,
    activeDurationMilliseconds: active,
    outcome,
    ...(draft.originPlan ? { originPlan: draft.originPlan } : {}),
    ...(draft.executedPlan ? { executedPlan: draft.executedPlan } : {}),
    executionEntries: entries,
    ...(draft.migration ? { migration: draft.migration } : {}),
    ...(draft.timingEvidence ? { timingEvidence: draft.timingEvidence } : {}),
    createdAt,
    updatedAt: laterEffortInstant(createdAt, canonicalizeEffortInstant(draft.updatedAt)),
  };

  return fields as EffortSession;
}

/**
 * Re-seals bytes that are already in History. Safe by construction: a row can only
 * exist in the effort store because it went through {@link sealEffortSession}
 * once, so this door cannot launder a discarded effort into a record.
 */
export function rehydrateEffortSession(fields: EffortSessionFields): EffortSession {
  return fields as EffortSession;
}

/**
 * The result of ending an effort. `retryPending` is not an error state — it is the
 * fail-closed guarantee: the work is still held on this device with its original
 * frozen end time, and a later attempt can complete the archive.
 */
export type EffortEndResult =
  | { readonly kind: "archived"; readonly session: EffortSession }
  | { readonly kind: "discarded"; readonly discarded: DiscardedEffort }
  | {
      readonly kind: "retryPending";
      readonly termination: ArchivedTermination;
      readonly draft: EffortSessionDraft;
    };

/* ------------------------------------------------------------------------- *
 * Record validation
 * ------------------------------------------------------------------------- */

export type EffortValidationIssue =
  | { readonly kind: "legacyEstimatedEntry"; readonly entryId: ExecutionEntryId }
  | {
      readonly kind: "entryTotalMismatch";
      readonly entryTotal: Milliseconds;
      readonly activeDuration: Milliseconds;
    }
  | {
      readonly kind: "visitTotalMismatch";
      readonly entryId: ExecutionEntryId;
      readonly visitTotal: Milliseconds;
      readonly entryDuration: Milliseconds;
    }
  | { readonly kind: "visitsNotMonotonic"; readonly entryId: ExecutionEntryId }
  | { readonly kind: "visitOutsideActiveDuration"; readonly entryId: ExecutionEntryId }
  | {
      readonly kind: "visitsOverlap";
      readonly firstEntryId: ExecutionEntryId;
      readonly secondEntryId: ExecutionEntryId;
    }
  | { readonly kind: "activeExceedsWall" }
  | { readonly kind: "endedBeforeStarted" }
  | { readonly kind: "invalidAuditDates" }
  | { readonly kind: "missingTimingEvidence" }
  | { readonly kind: "invalidTimingEvidence" };

/**
 * Execution validation is skipped wholesale when a migration envelope exists — the
 * legacy store predates every per-entry rule below, and failing an old record here
 * would remove it from the user's training record rather than repair it.
 */
export function validateEffortSession(session: EffortSessionFields): EffortValidationIssue[] {
  const issues: EffortValidationIssue[] = [];

  if (session.endedAt < session.startedAt) issues.push({ kind: "endedBeforeStarted" });
  if (session.activeDurationMilliseconds > session.wallDurationMilliseconds) {
    issues.push({ kind: "activeExceedsWall" });
  }
  // A corrected end may move past createdAt but never past updatedAt: a record
  // cannot describe effort that finished after the record was last written.
  if (session.updatedAt < session.createdAt || session.updatedAt < session.endedAt) {
    issues.push({ kind: "invalidAuditDates" });
  }
  if (session.source === "manual" && session.timingEvidence === undefined) {
    issues.push({ kind: "missingTimingEvidence" });
  }
  const offset = session.timingEvidence?.entryGMTOffsetSecondsAtStart;
  if (offset !== undefined && Math.abs(offset) > MAXIMUM_GMT_OFFSET_SECONDS) {
    issues.push({ kind: "invalidTimingEvidence" });
  }

  if (session.migration) return issues;

  const active = session.activeDurationMilliseconds;
  let entryTotal = 0;
  for (const entry of session.executionEntries) {
    if (entry.fidelity === "legacyEstimated") {
      issues.push({ kind: "legacyEstimatedEntry", entryId: entry.id });
    }
    entryTotal += entry.performedMilliseconds;

    const visitTotal = entry.visits.reduce((sum, visit) => sum + visit.durationMilliseconds, 0);
    if (entry.visits.length > 0 && visitTotal !== entry.performedMilliseconds) {
      issues.push({
        kind: "visitTotalMismatch",
        entryId: entry.id,
        visitTotal,
        entryDuration: entry.performedMilliseconds,
      });
    }

    let cursor = -1;
    for (const visit of entry.visits) {
      if (visit.offsetMilliseconds < cursor) {
        issues.push({ kind: "visitsNotMonotonic", entryId: entry.id });
        break;
      }
      cursor = visit.offsetMilliseconds + visit.durationMilliseconds;
    }
    for (const visit of entry.visits) {
      if (
        visit.offsetMilliseconds < 0 ||
        visit.offsetMilliseconds + visit.durationMilliseconds > active
      ) {
        issues.push({ kind: "visitOutsideActiveDuration", entryId: entry.id });
        break;
      }
    }
  }

  // Only meaningful against an executed ledger. A manual retrospective record and
  // an open-effort (count-up) record both have no plan to attribute time to and
  // therefore no entries at all — asserting `0 === active` on them would reject
  // the two planless shapes the product's own constructors produce.
  if (session.executionEntries.length > 0 && entryTotal !== active) {
    issues.push({ kind: "entryTotalMismatch", entryTotal, activeDuration: active });
  }

  // Two steps cannot occupy the same instant of active time; an overlap means the
  // ledger double-counted and every breakdown built from it would over-report.
  const spans = session.executionEntries.flatMap((entry) =>
    entry.visits.map((visit) => ({
      entryId: entry.id,
      start: visit.offsetMilliseconds,
      end: visit.offsetMilliseconds + visit.durationMilliseconds,
    })),
  );
  spans.sort((left, right) => left.start - right.start || left.end - right.end);
  for (let index = 1; index < spans.length; index += 1) {
    const previous = spans[index - 1];
    const current = spans[index];
    if (!previous || !current) continue;
    if (current.start < previous.end && current.entryId !== previous.entryId) {
      issues.push({
        kind: "visitsOverlap",
        firstEntryId: previous.entryId,
        secondEntryId: current.entryId,
      });
    }
  }

  return issues;
}

/* ------------------------------------------------------------------------- *
 * Live phase presentation
 * ------------------------------------------------------------------------- */

export type LivePhaseTint = "signal" | "sky" | "violet" | "volt" | "chalk" | "warning";

/**
 * History and Summary reuse the live phase tints plus `fog`, which never appears
 * as a live phase colour and exists only to mark a partial outcome as neutral
 * rather than as a warning.
 */
export type EffortTint = LivePhaseTint | "fog";

export type BurstPhase = "work" | "movingReset";

export const SEGMENT_KIND_TINTS: Readonly<Record<SegmentKind, LivePhaseTint>> = Object.freeze({
  preparation: "chalk",
  warmup: "violet",
  work: "signal",
  recovery: "sky",
  strength: "volt",
  cooldown: "violet",
});

/**
 * Precedence, not a lookup: the final-ten warning outranks the burst reset, which
 * outranks the segment's own kind, because urgency has to survive whatever phase
 * the user happens to be in.
 */
export function livePhaseTint(
  kind: SegmentKind,
  options: { readonly isFinalTen: boolean; readonly isBurstReset: boolean },
): LivePhaseTint {
  if (options.isFinalTen) return "warning";
  if (options.isBurstReset) return "sky";
  return SEGMENT_KIND_TINTS[kind];
}

export const LIVE_REMAINING_LABELS = Object.freeze({
  timeCap: "Time cap remaining",
  preparation: "Preparation remaining",
  warmup: "Warm-up remaining",
  boxingRound: "Round remaining",
  work: "Work remaining",
  recovery: "Recovery remaining",
  strength: "Strength remaining",
  cooldown: "Cool-down remaining",
});

/** A rep-target interval is capped, not measured, so its clock counts down a cap
 *  rather than a prescribed working duration. */
export function liveRemainingLabel(segment: SessionSegment): string {
  if (segmentRepetitionTarget(segment) !== undefined) return LIVE_REMAINING_LABELS.timeCap;
  switch (segment.kind) {
    case "preparation":
      return LIVE_REMAINING_LABELS.preparation;
    case "warmup":
      return LIVE_REMAINING_LABELS.warmup;
    case "work":
      return isBoxingRoundSegment(segment)
        ? LIVE_REMAINING_LABELS.boxingRound
        : LIVE_REMAINING_LABELS.work;
    case "recovery":
      return LIVE_REMAINING_LABELS.recovery;
    case "strength":
      return LIVE_REMAINING_LABELS.strength;
    case "cooldown":
      return LIVE_REMAINING_LABELS.cooldown;
  }
}

export type LiveDurationScope = "cap" | "round" | "interval";

export function liveDurationScope(segment: SessionSegment): LiveDurationScope {
  if (segmentRepetitionTarget(segment) !== undefined) return "cap";
  return isBoxingRoundSegment(segment) ? "round" : "interval";
}

export const LIVE_FINAL_TEN_BADGE = "Final ten";

export const BURST_PHASE_LABELS: Readonly<Record<BurstPhase, string>> = Object.freeze({
  work: "WORK",
  movingReset: "MOVING RESET",
});

/**
 * Spoken in the foreground while the user is looking at the clock. Deliberately
 * distinct from the lock-screen notification copy the SETTINGS context owns: a
 * notification is readable by anyone holding the device and must stay generic,
 * while these lines are heard by the person training.
 */
export const LIVE_SPOKEN_CUES = Object.freeze({
  halfway: "Halfway",
  /** Literal by contract: the spoken line stays "Ten seconds" even when the
   *  configured threshold is not ten, because the phrase itself is fixed. */
  finalCountdown: "Ten seconds",
  paused: "Paused",
  resumed: "Resumed",
  workoutComplete: "Workout complete",
  workoutEnded: "Workout ended",
  burstReset: "Reset. Stay moving.",
  burstWork: "Work",
});

export function nextDrillAnnouncement(drillName: string): string {
  return `Next, ${drillName}`;
}

/**
 * Hardcoded rotations, deliberately: these are coaching scripts rather than user
 * data, and rotating them modularly keeps a long segment from repeating one
 * movement for its whole duration.
 */
export const WARMUP_DRILL_ROTATION: readonly string[] = Object.freeze([
  "Boxer-step jump rope",
  "Shoulder + hip mobility",
  "Boxer bounce",
  "Light shadowboxing",
  "Straight shots + footwork",
]);

export const STRENGTH_DRILL_ROTATION: readonly string[] = Object.freeze([
  "Forearm plank",
  "Dead bugs",
  "Bicycle crunches",
  "Push-up + shoulder tap",
]);

export const COOLDOWN_DRILL_ROTATION: readonly string[] = Object.freeze([
  "Walk + breathe",
  "Shoulder stretch",
  "Hip flexor stretch",
  "Calf stretch",
  "Box breathing",
]);

/* ------------------------------------------------------------------------- *
 * Live snapshots and checkpoints
 * ------------------------------------------------------------------------- */

/**
 * The structured checkpoint. Every stored number is clamped on load rather than
 * trusted, because a checkpoint is the one input that survives a crash: it has to
 * self-heal into a runnable state instead of propagating corruption into a record.
 */
export interface StructuredEffortCheckpoint {
  readonly shape: "structured";
  readonly schemaVersion: number;
  readonly id: EffortCheckpointId;
  readonly sessionId: EffortSessionId;
  readonly startedAt: EffortInstant;
  /** Forced to at least `startedAt`. */
  readonly lastUpdated: EffortInstant;
  readonly plan: SessionPlan;
  /** Clamped into the plan's segment range. */
  readonly segmentIndex: number;
  /** Clamped to 0…the current segment's duration. */
  readonly remaining: Milliseconds;
  /** Forced false once finished, so a restored terminal checkpoint cannot tick. */
  readonly isRunning: boolean;
  readonly isFinished: boolean;
  readonly elapsed: Milliseconds;
  /** Clamped to the plan's `workRounds`. */
  readonly completedRounds: number;
  /** Floored at 0; counts skipped *combination presentations*, not intervals. */
  readonly skippedCombos: number;
  readonly combo?: ComboScore;
  readonly activeDrill?: string;
  readonly executionLedger: WorkoutExecutionLedger;
  /** Retained only for a finished checkpoint whose archive failed; its presence is
   *  the signal that an idempotent retry is owed. */
  readonly terminalOutcome?: EffortOutcome;
}

/**
 * The count-up checkpoint.
 *
 * Active time is reconstructed from a monotonic source plus an estimated boot
 * instant, never from wall time. That is the anti-fabrication rule of this clock:
 * a wall-clock jump adds elapsed time but must never invent active time, and a
 * reboot must not erase active time already earned.
 */
export interface OpenEffortCheckpoint {
  readonly shape: "duration";
  readonly schemaVersion: number;
  readonly id: EffortCheckpointId;
  readonly sessionId: EffortSessionId;
  readonly activity: ActivitySnapshot;
  /** Frozen when the effort started, so `deviceAuthority` is a recorded fact at
   *  archive time rather than something re-guessed from whatever device retried. */
  readonly provenance: EffortProvenance;
  readonly startedAt: EffortInstant;
  readonly lastUpdated: EffortInstant;
  readonly activeDurationMilliseconds: Milliseconds;
  readonly wallDurationMilliseconds: Milliseconds;
  readonly isRunning: boolean;
  readonly isFinished: boolean;
  readonly monotonicUptimeMilliseconds: Milliseconds;
  readonly estimatedBootInstant: EffortInstant;
  /** Retained only for a finished checkpoint whose archive failed; its presence is
   *  the signal that an idempotent retry is owed, and it carries the original
   *  frozen `endedAt` so the retry cannot move when the user trained. The
   *  structured checkpoint's `terminalOutcome` plays the same role. */
  readonly terminalSession?: EffortSession;
}

export type EffortCheckpoint = StructuredEffortCheckpoint | OpenEffortCheckpoint;

/**
 * What the live surface renders each tick. Purely derived from the checkpoint and
 * the clock, so it is never persisted and never a source of truth.
 */
export interface LiveEffortSnapshot {
  readonly sessionId: EffortSessionId;
  readonly at: EffortInstant;
  readonly segment: SessionSegment;
  readonly segmentIndex: number;
  readonly segmentCount: number;
  readonly remaining: Milliseconds;
  readonly elapsedInSegment: Milliseconds;
  readonly elapsedInSession: Milliseconds;
  readonly isRunning: boolean;
  readonly isFinished: boolean;
  readonly isLocked: boolean;
  readonly isFinalTen: boolean;
  readonly burstPhase?: BurstPhase;
  readonly burstRemaining?: Milliseconds;
  readonly tint: LivePhaseTint;
  readonly combo?: ComboScore;
  readonly activeDrill?: string;
  readonly nextSegmentTitle?: string;
}

/** The count-up equivalent, which has no plan and therefore no progress at all —
 *  a count-up clock has nothing to be a fraction of. */
export interface OpenEffortSnapshot {
  readonly sessionId: EffortSessionId;
  readonly at: EffortInstant;
  readonly activity: ActivitySnapshot;
  readonly activeDuration: Milliseconds;
  readonly wallDuration: Milliseconds;
  readonly isRunning: boolean;
  readonly isLocked: boolean;
}

/** Exactly 1 for a zero-duration segment: it is over the instant it begins, and a
 *  0/0 progress must not read as "not started". */
export function segmentProgress(remaining: Milliseconds, duration: Milliseconds): number {
  if (duration <= 0) return 1;
  return Math.min(1, Math.max(0, 1 - remaining / duration));
}

export function overallProgress(elapsed: Milliseconds, planDuration: Milliseconds): number {
  if (planDuration <= 0) return 1;
  return Math.min(1, Math.max(0, elapsed / planDuration));
}

export const LIVE_NEXT_SEGMENT_FALLBACK = "Session complete";

/* ------------------------------------------------------------------------- *
 * Session reflection
 * ------------------------------------------------------------------------- */

/** 1…10 as literals rather than a validated number, so an out-of-range rating is
 *  unrepresentable instead of merely rejected. */
export type PerceivedEffort = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/** Adding a rating seeds the midpoint rather than the minimum, so the first drag
 *  can move either way and the seed is not mistaken for a deliberate "1". */
export const PERCEIVED_EFFORT_SEED: PerceivedEffort = 5;

/**
 * One reflection per session, enforced by using the session id as the reflection's
 * own id — a second reflection for the same session is not a duplicate row, it is
 * the same row.
 */
export interface SessionReflection {
  readonly schemaVersion: number;
  readonly sessionId: EffortSessionId;
  readonly createdAt: EffortInstant;
  readonly updatedAt: EffortInstant;
  readonly perceivedEffort?: PerceivedEffort;
  readonly note?: string;
}

export function normalizeCornerNote(raw: string): string | undefined {
  const trimmed = raw.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/** Truncated as typed rather than validated after the fact — there is deliberately
 *  no "note too long" error for the user to recover from. */
export function truncateCornerNote(raw: string): string {
  return Array.from(raw).slice(0, CORNER_NOTE_CHARACTER_LIMIT).join("");
}

/**
 * Returns `null` when there is nothing to remember. "Skip this reflection" must
 * create no record at all, so an empty note with no rating is a clear rather than
 * an empty row that would later render as saved-but-blank.
 */
export function makeSessionReflection(input: {
  readonly sessionId: EffortSessionId;
  readonly createdAt: EffortInstant;
  readonly updatedAt: EffortInstant;
  readonly perceivedEffort?: PerceivedEffort;
  readonly note?: string;
  readonly schemaVersion?: number;
}): SessionReflection | null {
  const note = input.note === undefined ? undefined : normalizeCornerNote(input.note);
  if (input.perceivedEffort === undefined && note === undefined) return null;

  const createdAt = canonicalizeEffortInstant(input.createdAt);
  return {
    schemaVersion: Math.max(
      1,
      Math.trunc(input.schemaVersion ?? SESSION_REFLECTION_SCHEMA_VERSION),
    ),
    sessionId: input.sessionId,
    createdAt,
    updatedAt: laterEffortInstant(createdAt, canonicalizeEffortInstant(input.updatedAt)),
    ...(input.perceivedEffort !== undefined ? { perceivedEffort: input.perceivedEffort } : {}),
    ...(note !== undefined ? { note } : {}),
  };
}

/** The one-line form used on the Home recent-training card. */
export function reflectionSummaryLine(reflection: SessionReflection | undefined): string | null {
  if (!reflection) return null;
  const { perceivedEffort, note } = reflection;
  if (perceivedEffort !== undefined && note) return `Effort ${perceivedEffort} of 10 · ${note}`;
  if (perceivedEffort !== undefined) return `Effort ${perceivedEffort} of 10`;
  if (note) return note;
  return null;
}

export interface SessionReflectionDraft {
  readonly note: string;
  readonly perceivedEffort?: PerceivedEffort;
}

/** Compared against the *normalized* note, so re-saving text that differs only by
 *  trailing whitespace is correctly recognized as clean. */
export function isReflectionDirty(
  stored: SessionReflection | undefined,
  draft: SessionReflectionDraft,
): boolean {
  return (
    stored?.note !== normalizeCornerNote(draft.note) ||
    stored?.perceivedEffort !== draft.perceivedEffort
  );
}

export const REFLECTION_COPY = Object.freeze({
  perceivedEffortHeading: "How hard did that feel?",
  perceivedEffortSubtitle: "Your perception, not a score.",
  addRating: "Add effort rating",
  removeRating: "Remove rating",
  perceivedEffortAccessibilityLabel: "Perceived effort",
  cornerNoteHeading: "Corner note",
  cornerNotePrompt: "What do you want to remember from this effort?",
  cornerNotePlaceholderLegacy: "Form, energy, a win, or something to revisit…",
  cornerNotePlaceholderCanonical: "Form, energy, a win, an injury, or something to revisit…",
  save: "Save reflection",
  update: "Update reflection",
  clear: "Clear",
  clearAccessibilityLabel: "Clear reflection",
  statusSkip: "Skip this reflection and continue.",
  statusNoteOptional: "The note is optional.",
  statusCleared: "Reflection cleared.",
  statusNoneSaved: "No reflection saved.",
  saveFailed: "Couldn't save the note. Your text is still here.",
});

export function reflectionCharacterStatus(noteLength: number): string {
  return `${noteLength} of 1,200 characters`;
}

export function reflectionSavedStatus(deviceNoun: string): string {
  return `Saved privately on ${deviceNoun}.`;
}

/* ------------------------------------------------------------------------- *
 * Duration formatting used by effort surfaces
 * ------------------------------------------------------------------------- */

/**
 * History's clock: seconds round **up** and minutes are **not** capped at 59, so a
 * 90-minute session reads "90:00". Rounding up means a session never displays a
 * smaller number than the time actually spent.
 *
 * Deliberately distinct from `formatDuration` in time.ts, which rounds to nearest
 * and rolls into hours. Both appear in the app and they are not interchangeable.
 */
export function formatEffortClock(duration: Milliseconds): string {
  const totalSeconds = Math.max(0, Math.ceil(duration / SECOND));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * The Home card's compact form, which **truncates** where the clock rounds up. The
 * asymmetry is intentional: a glanceable summary should not claim a minute the
 * user did not finish.
 */
export function formatEffortCompact(duration: Milliseconds): string {
  const totalSeconds = Math.max(0, Math.trunc(duration / SECOND));
  if (totalSeconds < 60) return `${totalSeconds} sec`;
  const minutes = Math.trunc(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return seconds === 0 ? `${minutes} min` : `${minutes} min ${seconds} sec`;
}

/* ------------------------------------------------------------------------- *
 * History listing, totals, and temporal grouping
 * ------------------------------------------------------------------------- */

/**
 * Newest first, ties broken by id ascending. The tiebreak exists because two
 * sessions can share an `endedAt` to the millisecond once manual entry is
 * involved, and an unstable order would make rows jump between refreshes.
 */
export function compareHistoryNewestFirst(
  left: EffortSessionFields,
  right: EffortSessionFields,
): number {
  if (left.endedAt !== right.endedAt) return right.endedAt - left.endedAt;
  return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
}

/**
 * A flat, reverse-chronological list. There is deliberately no date grouping, no
 * section header, no filter, no search, no pagination, and no record cap: the
 * training record is a continuous log, and slicing it into buckets would imply the
 * app has an opinion about which sessions belong together.
 */
export function sortHistoryNewestFirst(
  sessions: readonly EffortSessionFields[],
): EffortSessionFields[] {
  return [...sessions].sort(compareHistoryNewestFirst);
}

export interface EffortTotals {
  readonly sessionCount: number;
  readonly completedCount: number;
  readonly partialCount: number;
  readonly recordedCount: number;
  /** Active seconds summed first, then divided by 60 and truncated. */
  readonly activeMinutes: number;
  readonly rounds: number;
}

/**
 * Wall time is deliberately absent from every total. Elapsed time includes rest,
 * travel, and forgotten pauses; only active time is a claim the app can stand
 * behind, so totals count nothing else.
 */
export function computeEffortTotals(sessions: readonly EffortSessionFields[]): EffortTotals {
  let activeSeconds = 0;
  let rounds = 0;
  let completedCount = 0;
  let partialCount = 0;
  let recordedCount = 0;

  for (const session of sessions) {
    activeSeconds += session.activeDurationMilliseconds / SECOND;
    rounds += completedRounds(session);
    if (session.outcome === "completed") completedCount += 1;
    else if (session.outcome === "partial") partialCount += 1;
    else recordedCount += 1;
  }

  return {
    sessionCount: sessions.length,
    completedCount,
    partialCount,
    recordedCount,
    activeMinutes: Math.trunc(activeSeconds / 60),
    rounds,
  };
}

/**
 * Prefers the legacy count when a migration envelope carries one, because the
 * legacy record counted rounds in a way the entry-based rule cannot reconstruct.
 * Planless and manual sessions have no executed plan and therefore contribute
 * nothing — there are no rounds to count, which is not the same as zero performed.
 */
export function completedRounds(session: EffortSessionFields): number {
  const legacy = session.migration?.legacyCompletedRounds;
  if (legacy !== undefined) return Math.max(0, Math.trunc(legacy));

  const segments = session.executedPlan?.segments;
  if (!segments) return 0;

  const credited = new Set<SessionSegmentId>(
    session.executionEntries
      .filter(
        (entry) =>
          entry.disposition === "completedNaturally" || entry.disposition === "manualCompletion",
      )
      .map((entry) => entry.segmentId),
  );
  return segments.filter((segment) => isBoxingRoundSegment(segment) && credited.has(segment.id))
    .length;
}

/**
 * One day of the weekly rhythm chart. Day boundaries are supplied by the caller
 * because week start and day start are locale- and calendar-dependent, and the
 * domain layer must not consult a calendar.
 *
 * This is the *only* temporal aggregation in the effort context. There is
 * deliberately no streak, no consistency score, and no "days since": the product
 * does not gamify attendance, and a derived streak here is the cheapest way to
 * accidentally ship one.
 */
export interface EffortDayBucket {
  readonly dayStart: EffortInstant;
  readonly nextDayStart: EffortInstant;
  readonly minutes: number;
}

export function effortWeekRhythm(
  sessions: readonly EffortSessionFields[],
  dayBoundaries: readonly EffortInstant[],
): EffortDayBucket[] {
  const buckets: EffortDayBucket[] = [];
  for (let index = 0; index < dayBoundaries.length; index += 1) {
    const dayStart = dayBoundaries[index];
    const nextDayStart = dayBoundaries[index + 1];
    if (dayStart === undefined || nextDayStart === undefined) continue;

    const active = sessions
      .filter((session) => session.endedAt >= dayStart && session.endedAt < nextDayStart)
      .reduce((sum, session) => sum + session.activeDurationMilliseconds, 0);

    buckets.push({
      dayStart,
      nextDayStart,
      minutes: Math.min(WEEKLY_RHYTHM_MINUTE_CEILING, Math.trunc(active / (60 * SECOND))),
    });
  }
  return buckets;
}

/* ------------------------------------------------------------------------- *
 * Post-session summary
 * ------------------------------------------------------------------------- */

/**
 * Structured mode is gated on `inAppPlan` *and* an executed plan, so a migrated,
 * manual, or imported session can never render a rounds-and-breakdown docket it
 * has no evidence for.
 */
export function summaryShape(session: EffortSessionFields): "structured" | "planless" {
  return session.source === "inAppPlan" && session.executedPlan !== undefined
    ? "structured"
    : "planless";
}

/**
 * Whether Elapsed and Paused may be shown at all.
 *
 * For a manual session, `wall === active` is a documented assumption rather than
 * proof of continuity, so a manual record with no pause information shows neither
 * metric. Printing "0:00 paused" would present an assumption as a measurement.
 */
export function showsElapsedEvidence(session: EffortSessionFields): boolean {
  if (session.source !== "manual") return true;
  return hasUserEnteredPause(session.timingEvidence);
}

export type PlanlessRowMetric = "active" | "elapsed" | "paused" | "source";

/** Metric identity is its label, so a label may never repeat within a row. */
export function planlessRowMetrics(session: EffortSessionFields): PlanlessRowMetric[] {
  const metrics: PlanlessRowMetric[] = ["active"];
  const pauseKnown = hasUserEnteredPause(session.timingEvidence);
  if (session.source !== "manual" || pauseKnown) metrics.push("elapsed");
  if (session.source === "manual" && pauseKnown) metrics.push("paused");
  metrics.push("source");
  return metrics;
}

export interface StructuredSummaryDocket {
  readonly activeDuration: Milliseconds;
  readonly plannedDuration: Milliseconds;
  readonly completedRounds: number;
  readonly workRounds: number;
}

export function structuredSummaryDocket(
  session: EffortSessionFields,
  plan: SessionPlan,
): StructuredSummaryDocket {
  return {
    activeDuration: session.activeDurationMilliseconds,
    plannedDuration: plan.totalDuration,
    // Clamped because the ledger can credit a revisited round more than once while
    // the plan still contains only the rounds it declared.
    completedRounds: Math.min(plan.workRounds, completedRounds(session)),
    workRounds: plan.workRounds,
  };
}

export interface EffortPhaseBreakdownRow {
  readonly kind: SegmentKind;
  readonly title: string;
  readonly plannedDuration: Milliseconds;
  readonly performedDuration: Milliseconds;
  /** Clamped to 1: revisiting work legitimately exceeds the plan, but a bar that
   *  overflows its track reads as a rendering bug rather than as extra effort. */
  readonly progress: number;
  readonly tint: LivePhaseTint;
}

export const BREAKDOWN_TITLES = Object.freeze({
  preparation: "Preparation",
  warmup: "Warm-up",
  guidedWork: "Bag rounds",
  work: "Rounds",
  recovery: "Recovery",
  strength: "Core + strength",
  cooldown: "Cool-down",
});

export function breakdownTitle(kind: SegmentKind, mode: WorkoutMode): string {
  switch (kind) {
    case "preparation":
      return BREAKDOWN_TITLES.preparation;
    case "warmup":
      return BREAKDOWN_TITLES.warmup;
    case "work":
      return mode === "guided" ? BREAKDOWN_TITLES.guidedWork : BREAKDOWN_TITLES.work;
    case "recovery":
      return BREAKDOWN_TITLES.recovery;
    case "strength":
      return BREAKDOWN_TITLES.strength;
    case "cooldown":
      return BREAKDOWN_TITLES.cooldown;
  }
}

/**
 * Aggregated by segment *kind* in first-appearance order, not by segment, so a
 * twelve-round workout reads as one "Bag rounds" row instead of twelve. Rows with
 * no planned time are dropped rather than shown as 0/0.
 */
export function effortPhaseBreakdown(
  plan: SessionPlan,
  entries: readonly ExecutionEntry[],
): EffortPhaseBreakdownRow[] {
  const order: SegmentKind[] = [];
  const planned = new Map<SegmentKind, Milliseconds>();
  const performed = new Map<SegmentKind, Milliseconds>();
  const kindBySegment = new Map<SessionSegmentId, SegmentKind>();

  for (const segment of plan.segments) {
    if (!planned.has(segment.kind)) {
      order.push(segment.kind);
      planned.set(segment.kind, 0);
      performed.set(segment.kind, 0);
    }
    planned.set(segment.kind, (planned.get(segment.kind) ?? 0) + Math.max(0, segment.duration));
    kindBySegment.set(segment.id, segment.kind);
  }

  for (const entry of entries) {
    const kind = kindBySegment.get(entry.segmentId);
    if (kind === undefined) continue;
    performed.set(kind, (performed.get(kind) ?? 0) + entry.performedMilliseconds);
  }

  const rows: EffortPhaseBreakdownRow[] = [];
  for (const kind of order) {
    const plannedDuration = planned.get(kind) ?? 0;
    if (plannedDuration <= 0) continue;
    const performedDuration = performed.get(kind) ?? 0;
    rows.push({
      kind,
      title: breakdownTitle(kind, plan.mode),
      plannedDuration,
      performedDuration,
      progress: Math.min(1, Math.max(0, performedDuration / plannedDuration)),
      tint: SEGMENT_KIND_TINTS[kind],
    });
  }
  return rows;
}

/* ------------------------------------------------------------------------- *
 * Manual retrospective effort
 * ------------------------------------------------------------------------- */

/**
 * How an entered local start resolves against the selected zone.
 *
 * A spring-forward gap and a fall-back overlap are first-class states rather than
 * silently resolved: shifting the user's time by an hour to make it valid would
 * rewrite what they said they did, and picking one of two real instants for them
 * would guess.
 */
export type LocalStartResolution =
  | {
      readonly kind: "resolved";
      readonly instant: EffortInstant;
      readonly gmtOffsetSeconds: number;
    }
  | { readonly kind: "nonexistentLocalStart" }
  | {
      readonly kind: "ambiguousLocalStart";
      readonly earlier: EffortInstant;
      readonly later: EffortInstant;
      /** Absent until the user chooses. The choice resets whenever the date, time,
       *  or zone changes, because it no longer refers to the same pair. */
      readonly chosen?: "earlier" | "later";
    };

export function resolvedLocalStart(resolution: LocalStartResolution): EffortInstant | undefined {
  if (resolution.kind === "resolved") return resolution.instant;
  if (resolution.kind === "ambiguousLocalStart" && resolution.chosen) {
    return resolution.chosen === "earlier" ? resolution.earlier : resolution.later;
  }
  return undefined;
}

export type ManualEffortField =
  | "activity"
  | "activityName"
  | "startedAt"
  | "activeDuration"
  | "pauseDuration"
  | "status";

export type ManualEffortValidationError =
  | { readonly kind: "missingActivity" }
  | { readonly kind: "invalidActivity" }
  | { readonly kind: "catalogSnapshotMismatch" }
  | { readonly kind: "invalidTimeZoneIdentifier" }
  | { readonly kind: "nonexistentLocalStart" }
  | { readonly kind: "ambiguousLocalStart" }
  | { readonly kind: "invalidTimingEvidence" }
  | { readonly kind: "startInFuture" }
  | { readonly kind: "endInFuture" }
  | { readonly kind: "invalidActiveDuration" }
  | { readonly kind: "invalidPauseDuration" }
  | { readonly kind: "invalidAuditDates" };

/**
 * Focus follows the error so a screen-reader user can actually finish the form —
 * without this mapping the inline message is announced with no way to reach the
 * field it describes.
 *
 * `invalidActivity` is the interesting case: it points at the name field only when
 * "Other" is selected, because otherwise there is no name field to focus.
 */
export function manualEffortFocusTarget(
  error: ManualEffortValidationError,
  isOtherActivitySelected: boolean,
): ManualEffortField {
  switch (error.kind) {
    case "missingActivity":
    case "catalogSnapshotMismatch":
      return "activity";
    case "invalidActivity":
      return isOtherActivitySelected ? "activityName" : "activity";
    case "invalidTimeZoneIdentifier":
    case "nonexistentLocalStart":
    case "ambiguousLocalStart":
    case "invalidTimingEvidence":
    case "startInFuture":
    case "endInFuture":
      return "startedAt";
    case "invalidPauseDuration":
      return "pauseDuration";
    case "invalidActiveDuration":
    case "invalidAuditDates":
      return "activeDuration";
  }
}

export interface ManualEffortTiming {
  readonly startedAt: EffortInstant;
  readonly activeDurationMilliseconds: Milliseconds;
  readonly wallDurationMilliseconds: Milliseconds;
  readonly endedAt: EffortInstant;
  readonly evidence: EffortTimingEvidence;
}

/**
 * The canonical manual mapping.
 *
 * `endedAt` is computed on the absolute timeline (`startedAt + wall`) rather than
 * by adding to wall-clock components, which is what makes an entry spanning a DST
 * transition end at the right real-world instant.
 *
 * With no pause disclosed, wall is *assumed* equal to active and pause accounting
 * stays `notProvided`. Recording that assumption in the evidence is what lets
 * History refuse to display an Elapsed figure it cannot prove.
 */
export function manualEffortTiming(input: {
  readonly startedAt: EffortInstant;
  readonly dosage: ManualEffortDosage;
  readonly entryTimeZoneIdentifier: string;
  readonly entryGMTOffsetSecondsAtStart: number;
  readonly durationInputResolutionMilliseconds: Milliseconds;
}): ManualEffortTiming {
  const wall = manualEffortWallDuration(input.dosage);
  const disclosesPause = input.dosage.kind === "activePlusPause";

  return {
    startedAt: input.startedAt,
    activeDurationMilliseconds: input.dosage.active,
    wallDurationMilliseconds: wall,
    endedAt: effortInstantAdvancedBy(input.startedAt, wall),
    evidence: {
      activeBasis: "userEntered",
      wallBasis: disclosesPause ? "activePlusEnteredPause" : "assumedEqualToActive",
      pauseAccounting: disclosesPause ? userEnteredPause(input.dosage.pause) : PAUSE_NOT_PROVIDED,
      durationInputResolutionMilliseconds: input.durationInputResolutionMilliseconds,
      entryTimeZoneIdentifier: input.entryTimeZoneIdentifier,
      entryGMTOffsetSecondsAtStart: input.entryGMTOffsetSecondsAtStart,
    },
  };
}

/**
 * A manual record is `recorded`, never completed or partial: there was no plan to
 * complete and no engine measured it. Its execution entries are empty for the same
 * reason — there is nothing to attribute the time to.
 */
export function manualEffortDraft(input: {
  readonly id: EffortSessionId;
  readonly activity: ActivitySnapshot;
  readonly timing: ManualEffortTiming;
  readonly deviceAuthority: DeviceAuthority;
  /** Both audit dates are the *verified save* time, not the form-open time. */
  readonly savedAt: EffortInstant;
  /** 1 on create; the prior revision plus one on a confirmed edit. */
  readonly revision?: number;
  /** Preserved unchanged across edits, so correcting a record does not restart its
   *  history at today's date. */
  readonly createdAt?: EffortInstant;
}): EffortSessionDraft {
  return {
    id: input.id,
    revision: input.revision ?? 1,
    activity: input.activity,
    source: "manual",
    provenance: { deviceAuthority: input.deviceAuthority },
    startedAt: input.timing.startedAt,
    wallDurationMilliseconds: input.timing.wallDurationMilliseconds,
    activeDurationMilliseconds: input.timing.activeDurationMilliseconds,
    requestedOutcome: "recorded",
    executionEntries: [],
    ledger: EMPTY_EXECUTION_LEDGER,
    timingEvidence: input.timing.evidence,
    createdAt: input.createdAt ?? input.savedAt,
    updatedAt: input.savedAt,
  };
}

/**
 * A manual save goes through the same seal as live effort, with the derived end
 * frozen into the termination — so the manual and engine paths cannot drift apart
 * in how a record comes into existence.
 */
export function manualEffortTermination(
  id: EffortSessionId,
  timing: ManualEffortTiming,
): ArchivedTermination {
  return { kind: "archived", sessionId: id, endedAt: timing.endedAt };
}

/**
 * `unavailable` (the store could not be read before the attempt) and
 * `saveNotConfirmed` (bytes were written but the verified read-back did not match)
 * are different facts with different retry semantics, so they are separate cases
 * rather than one generic failure.
 */
export type ManualSaveResult =
  | { readonly kind: "saved"; readonly session: EffortSession }
  | { readonly kind: "invalid"; readonly error: ManualEffortValidationError }
  | { readonly kind: "conflict"; readonly current: EffortSessionFields }
  | { readonly kind: "notFound" }
  | { readonly kind: "saveNotConfirmed" }
  | { readonly kind: "unavailable" };

export const MANUAL_SAVE_FAILURE_COPY = Object.freeze({
  conflict:
    "This effort changed elsewhere. Your edits are still here; reopen the current version and try again.",
  notFound: "This effort is no longer in History. Your edits are still here.",
  saveNotConfirmed:
    "Your save may have completed, but Fighting Form couldn't confirm it. Check History before trying again.",
  unavailable: "Training history is unavailable right now. Your details are still here; try again.",
  structural: "These details could not be saved. Review them and try again.",
});

export const MANUAL_EFFORT_COPY = Object.freeze({
  cancel: "Cancel manual entry",
  save: "Save effort",
  saveChanges: "Save changes",
  activeDurationAccessibilityLabel: "Active duration",
  timeZoneLabel: "Time zone",
  timeZoneSearchPrompt: "City or time zone",
  overlapWarning: "This local time happened twice when the clocks changed. Choose the one you mean.",
  checkHeading: "Check",
  checkSubheading: "Before saving",
  awaitingInput: "Enter active time to check when this effort ended.",
  editUnavailableTitle: "This record can't be edited",
  editUnavailableBody:
    "Fighting Form couldn't safely reconstruct the original entry. The saved session has not been changed.",
  editUnavailableCaption: "Original record preserved",
});

/* ------------------------------------------------------------------------- *
 * Ending, discarding, and recovering
 * ------------------------------------------------------------------------- */

export const END_EFFORT_COPY = Object.freeze({
  end: "End",
  finishTitle: "Finish this effort?",
  finishMessage: "Save the active time, return to the clock, or discard this effort.",
  save: "Save effort",
  keepGoing: "Keep going",
  discard: "Discard effort",
});

/**
 * The save-recovery screen. It is a full screen rather than an alert on purpose:
 * an alert invites a reflexive dismissal, and this is the moment the app has to
 * make good on the promise that a finished effort is never silently lost.
 */
export const SAVE_RECOVERY_COPY = Object.freeze({
  eyebrow: "SAVE CHECKPOINT",
  title: "Couldn't save yet",
  retry: "Try again",
});

export function saveRecoveryBody(deviceNoun: string): string {
  return `Your finished effort is still protected on ${deviceNoun}. Retry the archive now or come back to it from Home.`;
}

export function saveRecoveryKeepLocal(deviceNoun: string): string {
  return `Keep on ${deviceNoun}`;
}

export const EFFORT_FAILURE_COPY = Object.freeze({
  /** A start is refused outright when canonical history is unreadable, because an
   *  effort that could never be archived must not be allowed to begin. */
  startPreflight: "The workout could not be prepared. Nothing was replaced.",
  startTracking: "Couldn't start tracking. Your current effort was not changed.",
  checkpointFlush: "Couldn't update the saved checkpoint. Keep this screen open and retry.",
  restore: "The saved effort could not be opened. It has not been replaced or deleted.",
  replaceFailed:
    "The current effort could not be saved, so the new one did not start. Resume it and try again.",
});

/** One dialog with one vocabulary. The Swift builder ships a second, divergent
 *  version of this alert; that divergence is a known defect and is not reproduced. */
export const REPLACE_ACTIVE_EFFORT_COPY = Object.freeze({
  title: "Replace the active effort?",
  confirm: "Save current and start new",
  cancel: "Keep current effort",
  openEffortMessage: "The current effort will be saved as recorded before the new one begins.",
  structuredMessage:
    "The current workout will be saved as a partial session before the new one begins.",
});

export const UNREADABLE_CHECKPOINT_COPY = Object.freeze({
  title: "Discard unreadable saved effort?",
  confirm: "Discard and continue",
  cancel: "Keep saved effort",
  message:
    "Fighting Form can't read the saved effort. It has not been changed. Are you sure you want to discard it? This can't be undone.",
});

export function unreadableCheckpointFailure(deviceNoun: string): string {
  return `The unreadable saved effort could not be removed. It is still protected on ${deviceNoun}.`;
}

export const DIRTY_FORM_DISMISSAL_COPY = Object.freeze({
  title: "Discard these changes?",
  confirm: "Discard changes",
  cancel: "Keep editing",
  message: "This effort has not been saved.",
});

export const DISCARD_SETUP_COPY = Object.freeze({
  title: "Discard this setup?",
  confirm: "Discard setup",
  cancel: "Keep editing",
  message: "Your activity selection has not been started.",
});

/* ------------------------------------------------------------------------- *
 * History and summary copy
 * ------------------------------------------------------------------------- */

export const OUTCOME_PILL_COPY: Readonly<
  Record<
    EffortOutcome,
    { readonly label: string; readonly spokenLabel: string; readonly tint: EffortTint }
  >
> = Object.freeze({
  // The dot is decorative and hidden from assistive technology: colour is never
  // the only signal, so the text always carries the outcome on its own.
  completed: { label: "Completed", spokenLabel: "Completed workout", tint: "volt" },
  partial: { label: "Partial", spokenLabel: "Partial workout", tint: "fog" },
  recorded: { label: "Recorded", spokenLabel: "Recorded effort", tint: "sky" },
});

export const EFFORT_SOURCE_TITLES: Readonly<Record<EffortSource, string>> = Object.freeze({
  inAppPlan: "Fighting Form",
  inAppDuration: "Recorded here",
  manual: "Added manually",
  healthKit: "Apple Health",
});

export const HISTORY_COPY = Object.freeze({
  title: "History",
  eyebrow: "Training record",
  heading: "Effort over time",
  body: "Boxing sessions and the other work you record stay together with the reflections you choose to keep.",
  emptyTitle: "No sessions yet",
  emptyBody:
    "Finish a boxing workout or record another effort and its active time will appear here.",
  allSessions: "All sessions",
  recentSessions: "Recent sessions",
  newestFirst: "Newest first",
  totalsAccessibilityLabel: "Effort totals",
  sessionsTile: "Sessions",
  activeMinutesTile: "Active min",
  roundsTile: "Rounds",
  /** Never a guessed date: a manual record without entry evidence says so plainly
   *  rather than rendering the instant in whatever zone the device is in today. */
  recordedDateUnavailable: "Recorded date unavailable",
  clearHistory: "Clear training history",
  addEffort: "Add effort",
  details: "Details",
  repeat: "Repeat",
  edit: "Edit effort",
  deleteSession: "Delete session",
});

export function historyOutcomeTrailingLabel(totals: EffortTotals): string {
  return `${totals.completedCount} completed · ${totals.partialCount} partial · ${totals.recordedCount} recorded`;
}

export const HISTORY_DELETE_COPY = Object.freeze({
  title: "Delete this session?",
  confirm: "Delete session",
  cancel: "Keep session",
  failureTitle: "History wasn't changed",
  failureBody: "The session could not be deleted, so it remains in your training record.",
});

export function historyDeleteMessage(title: string, date: string): string {
  return `${title} from ${date} and its corner note will be removed. A separately saved Journal recap keeps its own workout snapshot until you delete that entry in Journal.`;
}

export function historyDeleteFallbackBody(deviceNoun: string): string {
  return `Your training record is still on ${deviceNoun}.`;
}

export const HISTORY_CLEAR_COPY = Object.freeze({
  title: "Clear training history?",
  confirm: "Clear all history",
  cancel: "Cancel",
  failureBody:
    "The complete history could not be cleared. Sessions that remain are still protected.",
});

export function historyClearMessage(deviceNoun: string): string {
  return `This removes every completed workout, recorded effort, and corner note from ${deviceNoun}. Saved plans and separately saved Journal entries remain.`;
}

export const SUMMARY_COPY = Object.freeze({
  title: "Summary",
  sessionResult: "Session result",
  breakdown: "Breakdown",
  breakdownTrailing: "Performed / planned",
  activeTime: "Active time",
  planned: "Planned",
  rounds: "Rounds",
  /** Legacy-only: the canonical structured docket has no skipped-combo metric. */
  skippedCombos: "Skipped combos",
  effortTime: "Effort time",
  elapsed: "Elapsed",
  paused: "Paused",
  completedHeadline: "Work complete",
  partialHeadline: "Work recorded",
  completedStatus:
    "The planned work was completed. Review the effort below, add a note if it helps, or run it again.",
  partialStatus:
    "The effort you performed is saved. Partial sessions remain part of your training record.",
  savedHeadline: "Effort saved",
  recordedHeadline: "Effort recorded",
  planlessStatus:
    "The active effort is in your training record. Add a private note if it will help the next session.",
  repeat: "Repeat this workout",
  done: "Done",
  writeFullRecap: "Write full recap",
  writeFullRecapDetail: "Add formatting, prompts, goals, photos, video, and more context.",
  continueFullRecap: "Continue full recap",
  reflect: "Reflect",
  journalTrailing: "Journal",
  optionalTrailing: "Optional",
});

/**
 * History renders a simple plan's mode as "Simple" while the Home saved-plan row
 * renders it as "Timer". The spec calls that a known copy divergence; the web
 * build resolves it in favour of History's vocabulary everywhere.
 */
export const SUMMARY_MODE_LABELS: Readonly<Record<WorkoutMode, string>> = Object.freeze({
  guided: "Guided",
  simple: "Simple",
});

export const RECAP_HANDOFF_COPY = Object.freeze({
  waitingTitle: "Your recap is waiting",
  retry: "Try again",
  keepForLater: "Keep for later",
  /** A failed Journal open never rolls back or weakens the already-saved effort. */
  fallbackBody: "Your effort is saved, and the recap request has not been discarded.",
  openFailed:
    "Journal could not open yet. Your effort is saved, and the recap request is still waiting.",
  prepareFailed: "Couldn't prepare the full recap. Your reflection is still here.",
  openRecapFailed: "Couldn't open the full recap. Your reflection is still here.",
});

export function fullRecapSourceOfTruth(noun: "session" | "effort"): string {
  return `Your full recap is the source of truth for this ${noun}.`;
}

export const HOME_RECENT_TRAINING_COPY = Object.freeze({
  sectionTitle: "Recent training",
  historyTrailing: "History",
  empty: "Completed, partial, and recorded efforts will appear here.",
  viewHistory: "View training history",
  firstRunIPad:
    "Your first saved effort will establish the rhythm here without changing the next workout on tape.",
  weekEyebrow: "THIS WEEK",
  weekHeading: "Training rhythm",
});

export const OPEN_EFFORT_LIVE_COPY = Object.freeze({
  activeTime: "ACTIVE TIME",
  activeTimeHeld: "ACTIVE TIME · HELD",
  activePill: "Active",
  pausedPill: "Paused",
});
